import { getExternalUrlAssetHref, isExternalUrlAsset, } from '#pplx/frontend/apps/ask/asset-utils/externalUrlAsset.js';
import { getExternalUrlLabel } from '#pplx/frontend/apps/ask/canvas-shared/externalUrl.js';
import { isPublishedAppUrl } from '#pplx/frontend/apps/ask/canvas-shared/publishedAppUrl.js';
import { getAssetTypeIcon } from '#pplx/frontend/apps/ask/canvas-shared/useAssetInfo.js';
import { getIconForFileType } from '#pplx/frontend/apps/ask/canvas-shared/utils/fileTypeUtils.js';
import { canonicalizeLegacyPermalinkPath } from '#pplx/frontend/apps/ask/permalink-slug/canonicalizeLegacyPermalink.js';
import { prettifyFilename } from '#pplx/frontend/apps/ask/shared-helpers/stringUtils.js';
import { getFileExtensionFromUrl } from '#pplx/frontend/common/files/urls.js';
import { getFileIcon } from '#pplx/frontend/components/utils/fileContentUtils.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
const CANVAS_ASSET_TYPES = new Set([
    'CODE_FILE',
    'RESEARCH_REPORT',
    'APP',
    'CHART',
    'GENERATED_IMAGE',
    'GENERATED_VIDEO',
    'PDF_FILE',
    'DOCX_FILE',
    'DOC_FILE',
    'XLSX_FILE',
    'AUDIO_FILE',
    'SLIDES',
    'MODEL_3D',
    'VISUALIZATION',
]);
export const getAssetComputerAppUrl = (asset) => canonicalizeLegacyPermalinkPath(`/computer/a/${asset.asset_slug ?? asset.asset_id}`);
const isCanvasAsset = (asset) => CANVAS_ASSET_TYPES.has(asset.asset_type);
export const getAssetDisplayName = (asset, fallback) => {
    if (isExternalUrlAsset(asset)) {
        return getExternalUrlLabel(asset.location, asset.caption) || fallback || '';
    }
    return asset.caption || asset.location?.split('/').pop() || asset.asset_id || fallback || '';
};
export const getHumanizedAssetDisplayName = (asset, fallback) => {
    const raw = getAssetDisplayName(asset, fallback);
    // A link's label is a display title, not a filename.
    if (isExternalUrlAsset(asset))
        return raw;
    const dotIndex = raw.lastIndexOf('.');
    const nameWithoutExt = dotIndex > 0 ? raw.slice(0, dotIndex) : raw;
    return prettifyFilename(nameWithoutExt);
};
export const isUserUploadedAsset = (asset) => asset.source === 'user-upload';
const isPublishedAppAsset = (asset) => asset.asset_type === 'APP' && isPublishedAppUrl(asset.location);
export const getAssetThreadUrl = (asset) => asset.source === 'asi' ? `/computer/tasks/${asset.entry_uuid}` : `/search/${asset.entry_uuid}`;
export const getAssetContentUrl = (asset) => {
    // Anchors built from this must not carry an unvalidated model-supplied
    // location, so an unopenable link points at the session like the click does.
    if (isExternalUrlAsset(asset)) {
        return getExternalUrlAssetHref(asset) ?? getAssetThreadUrl(asset);
    }
    if (isUserUploadedAsset(asset) && asset.location) {
        return asset.location;
    }
    if (isCanvasAsset(asset)) {
        return getAssetComputerAppUrl(asset);
    }
    return asset.location;
};
export const getAssetIcon = (asset) => {
    // Ahead of the extension probe: a link whose URL ends in `.pdf` is still a link.
    if (isExternalUrlAsset(asset)) {
        return getAssetTypeIcon(asset.asset_type);
    }
    if (isPublishedAppAsset(asset)) {
        return $icon('world');
    }
    if (isUserUploadedAsset(asset)) {
        if (asset.location) {
            return getFileIcon(asset.location);
        }
        return $icon('file-upload');
    }
    if (asset.location) {
        const fileIcon = getFileIcon(asset.location);
        if (fileIcon !== $icon('file')) {
            return fileIcon;
        }
    }
    if (asset.asset_type === 'CODE_FILE' || asset.asset_type === 'ASSET_DIFF') {
        const extension = getFileExtensionFromUrl(asset.location || asset.caption || '');
        const mimeType = asset.media_type?.trim().toLowerCase();
        if (asset.asset_type === 'CODE_FILE' &&
            (extension === 'zip' ||
                mimeType === 'application/zip' ||
                mimeType === 'application/x-zip-compressed')) {
            return getIconForFileType('zip');
        }
        return getIconForFileType(extension, getAssetTypeIcon(asset.asset_type));
    }
    return getAssetTypeIcon(asset.asset_type);
};
export const canViewAssetThread = (asset) => asset.viewer_relationship === 'OWNER' && Boolean(asset.entry_uuid) && !isUserUploadedAsset(asset);
export const getAssetPrimaryAction = (asset) => {
    // Ahead of the `source === 'asi'` branch, which would open the Computer session.
    if (isExternalUrlAsset(asset)) {
        const href = getExternalUrlAssetHref(asset);
        // Both paths return: an unopenable href must never fall through to window.open.
        return href
            ? { id: 'open', action: { type: 'external', url: href } }
            : { id: 'open', action: { type: 'navigate', url: getAssetThreadUrl(asset) } };
    }
    if (isUserUploadedAsset(asset) && asset.location) {
        return { id: 'open', action: { type: 'external', url: asset.location } };
    }
    if (isPublishedAppAsset(asset)) {
        return { id: 'open', action: { type: 'external', url: asset.location } };
    }
    if (asset.source === 'asi') {
        if (isCanvasAsset(asset)) {
            return { id: 'open', action: { type: 'external', url: getAssetComputerAppUrl(asset) } };
        }
        return {
            id: 'open',
            action: { type: 'navigate', url: `/computer/tasks/${asset.entry_uuid}` },
        };
    }
    if (isCanvasAsset(asset)) {
        return { id: 'open', action: { type: 'external', url: getAssetComputerAppUrl(asset) } };
    }
    if (asset.location) {
        return { id: 'open', action: { type: 'external', url: asset.location } };
    }
    return { id: 'open', action: { type: 'navigate', url: `/search/${asset.entry_uuid}` } };
};
export const getAssetViewThreadAction = (asset) => ({
    id: 'view-thread',
    action: { type: 'navigate', url: getAssetThreadUrl(asset) },
});
//# sourceMappingURL=assetHelpers.js.map